import CircleCheck from '@mui/icons-material/CheckCircle';
import Close from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Box,
  Card,
  Dialog,
  DialogContent,
  DialogTitle,
  GridLegacy as Grid,
  IconButton,
  Slide,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { TransitionProps } from '@mui/material/transitions';
import React, { useState } from 'react';

import useIsMobile from '../../hooks/useIsMobile';
import { theme } from '../../theme/theme';

const StyledCard = styled(Card)(() => ({
  borderRadius: '15px',
  padding: theme.spacing(5),
  boxShadow: 'unset',
  borderWidth: '1.5px',
  borderStyle: 'solid',
  borderColor: theme.palette.primary.main,
  textAlign: 'left',
}));

const StyledTypography = styled(Typography)(() => ({
  color: theme.palette.primary.main,
  backgroundColor: 'white',
  display: 'inline-block',
  position: 'absolute',
  margin: '-13px 0 0',
  transform: 'translateX(-50%)',
  padding: '0 5px',
}));

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const StyledDialogue = styled(Dialog)(({ theme }) => ({
  transition: 'all 0.3s ease',
  '& .MuiDialogTitle-root': {
    textAlign: 'center',
  },
  [theme.breakpoints.down('sm')]: {
    '& .MuiDialog-paper': {
      position: 'absolute',
      bottom: '0px',
      height: 'auto',
      borderRadius: '10px 10px 0px 0px',
    },
  },
}));

interface OwnProps {
  hideWeather: boolean;
}

const GuaranteeBox = (props: OwnProps) => {
  const { hideWeather } = props;
  const [selectedInfo, setSelectedInfo] = useState<keyof typeof Info>();
  const [showInfoModal, setShowInfoModal] = useState(false);
  const isMobile = useIsMobile();
  const closeInfoModal = () => {
    setShowInfoModal(false);
  };

  const showInfo = (category: keyof typeof Info) => {
    setSelectedInfo(category);
    setShowInfoModal(true);
  };
  const Header = {
    WEATHER: '100% Weather-Proof',
    QUALITY: 'Book with confidence',
    PRICE: 'Price-match promise',
  };

  const Info = {
    WEATHER: (
      <>
        <Typography p={1} fontSize={'14px'}>
          Weather dependent bookings are guaranteed to be risk-free for
          customers using Ellipsis Marine.
        </Typography>

        <Typography p={1} fontSize={'14px'}>
          If your booking needs to be adjusted due to unsuitable weather, your
          booking manager will reach out to you. You'll have the option to
          reschedule at a time convenient for you. If rescheduling isn't
          feasible, you'll receive a full refund, no matter the cancellation
          policy.
        </Typography>

        <Typography p={1} fontSize={'14px'}>
          While we can't guarantee perfect weather, we assure you that you'll
          only be charged if it's safe to proceed with your booking.
        </Typography>
      </>
    ),
    QUALITY: (
      <>
        <Typography p={1} fontSize={'14px'}>
          At Ellipsis Marine, we want to ensure your confidence in the service
          you receive on this platform. Here's how we guarantee your
          satisfaction:
        </Typography>

        <Typography variant={'h4'} fontSize={'14px'}>
          Service Guarantee:
        </Typography>
        <Typography p={1} fontSize={'14px'}>
          If an operator is not able to deliver the level of service as
          advertised at the time of booking, you're entitled to a refund.
        </Typography>

        <Typography variant={'h4'} fontSize={'14px'}>
          Clear Communication:
        </Typography>

        <Typography p={1} fontSize={'14px'}>
          Ellipsis Marine allows time for communications between all parties
          before we release your payment to the operator.
        </Typography>

        <Typography variant={'h4'} fontSize={'14px'}>
          Support Process:
        </Typography>
        <Typography p={1} fontSize={'14px'}>
          Should an issue arise, please notify the booking manager promptly.
          Provide detailed information to assist them in resolving the matter.
        </Typography>

        <Typography variant={'h4'} fontSize={'14px'}>
          Refund Policy:
        </Typography>
        <Typography p={1} fontSize={'14px'}>
          If the service falls short despite efforts to resolve, contact
          Ellipsis within 24 hours of booking completion. You must show that you
          have tried to resolve the issue with the listing provider in a timely
          manner. If the listing provider was given a fair opportunity and yet
          was unable to meet the level of service that was offered during the
          time of booking, you are entitled to a refund.
        </Typography>
      </>
    ),
    PRICE: (
      <>
        <Typography p={1} fontSize={'14px'}>
          If you see the same listing on another platform at a lower price,
          we’ll match it.
        </Typography>
      </>
    ),
  };

  return (
    <Box
      sx={{
        width: 'fit-content',
        marginTop: '30px',
        marginBottom: '30px',
        textAlign: 'center',
      }}
    >
      <StyledTypography variant={'h4'}>Our Guarantee</StyledTypography>
      <StyledCard>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} md={12}>
            <Typography p={1} fontSize={'14px'}>
              <CircleCheck style={{ fontSize: '1.1em' }} color="primary" /> You
              will only be charged if your booking is accepted.
            </Typography>
          </Grid>
          {!hideWeather && (
            <Grid item xs={12} sm={12} md={12}>
              <Typography p={1} fontSize={'14px'}>
                <CircleCheck style={{ fontSize: '1.1em' }} color="primary" />{' '}
                Weather-Proof Booking Assurance.
                <InfoOutlinedIcon
                  style={{ fontSize: '1.1em' }}
                  color="primary"
                  onClick={() => showInfo('WEATHER')}
                />
              </Typography>
            </Grid>
          )}
          <Grid item xs={12} sm={12} md={12}>
            <Typography p={1} fontSize={'14px'}>
              <CircleCheck style={{ fontSize: '1.1em' }} color="primary" />{' '}
              Quality Assurance Guarantee.
              <InfoOutlinedIcon
                style={{ fontSize: '1.1em' }}
                color="primary"
                onClick={() => showInfo('QUALITY')}
              />
            </Typography>
          </Grid>
          <Grid item xs={12} sm={12} md={12}>
            <Typography p={1} fontSize={'14px'}>
              <CircleCheck style={{ fontSize: '1.1em' }} color="primary" />{' '}
              Price Match Guarantee.
              <InfoOutlinedIcon
                style={{ fontSize: '1.1em' }}
                color="primary"
                onClick={() => showInfo('PRICE')}
              />
            </Typography>
          </Grid>
        </Grid>
      </StyledCard>
      <StyledDialogue
        fullScreen={isMobile}
        open={showInfoModal}
        onClose={() => closeInfoModal()}
        TransitionComponent={Transition}
        fullWidth
        maxWidth={'xs'}
        data-cy="authentication-dialogue"
      >
        <DialogTitle
          data-cy="dialogue-title"
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: isMobile ? 'space-between' : 'center',
          }}
        >
          <Typography variant="h2">
            {Header[selectedInfo as keyof typeof Header]}
          </Typography>
          {isMobile ? (
            <IconButton onClick={() => closeInfoModal()}>
              <Close
                sx={{
                  fontWeight: 'bold',
                  fontSize: 26,
                }}
              />
            </IconButton>
          ) : null}
        </DialogTitle>

        <DialogContent>
          <Grid container spacing={2} padding={'10px'}>
            <Grid item xs={12} sm={12} md={12}>
              {Info[selectedInfo as keyof typeof Info]}
            </Grid>
          </Grid>
        </DialogContent>
      </StyledDialogue>
    </Box>
  );
};

export default GuaranteeBox;
